import { Dispatch } from "react";

import { store } from "../../../app/store";
import { fetchEvaluations, selectEvaluations } from "../../../features/evaluation/evaluationsSlice";
import { fetchCertificatesListByUser, selectCertificateList } from "../../../features/students/certificateListSlice";
import {
  fetchProgressAuditFullByProgressId,
  selectProgressAuditFull,
} from "../../../features/progressAudit/progressAuditFullSlice";
import {
  fetchProgressAuditCertificateByHash,
  selectProgressAuditCertificate,
} from "../../../features/progressAudit/progressAuditCertificateSlice";
import { IProgressAuditTrace } from "../../../features/progressAudit/progressAuditTracesSlice";

// The full/course/lessons bundle only carries evaluation *ids* (course,
// topics and lessons each optionally reference one) - the evaluation
// documents themselves live in a different service/database, so they're
// fetched separately here and filtered down to just the ones this course
// actually references.
const collectRelevantEvaluationIds = (full: ReturnType<typeof selectProgressAuditFull>): Set<string> => {
  const ids = new Set<string>();
  const course = full?.course as { evaluation?: string; survey?: string } | undefined;
  if (course?.evaluation) {
    ids.add(course.evaluation);
  }
  if (course?.survey) {
    ids.add(course.survey);
  }
  (full?.topics || []).forEach((topic) => {
    const evaluationId = (topic as { evaluation?: string }).evaluation;
    if (evaluationId) {
      ids.add(evaluationId);
    }
  });
  (full?.lessons || []).forEach((lesson) => {
    const evaluationId = (lesson as { evaluation?: string }).evaluation;
    if (evaluationId) {
      ids.add(evaluationId);
    }
  });
  return ids;
};

export interface BuildCourseContextParams {
  progressId: string;
  username: string;
  traces: IProgressAuditTrace[];
}

// Assembles the "copiar contexto completo" JSON for the Progress Audit
// screen: course + topics + lessons + progress (one call, service-learning),
// the evaluations that course actually references (service-evaluation,
// filtered client-side out of the full list), the student's certificate for
// this course if one was issued (looked up by course title since
// /certificate/list/:username only returns courseName, not courseId), and
// the trace history already loaded in the screen.
export const buildCourseContextJson = async (
  dispatch: Dispatch<any>,
  { progressId, username, traces }: BuildCourseContextParams
): Promise<object> => {
  await dispatch(fetchProgressAuditFullByProgressId(progressId));
  const full = selectProgressAuditFull(store.getState());

  await dispatch(fetchEvaluations());
  const evaluations = selectEvaluations(store.getState());
  const relevantEvaluationIds = collectRelevantEvaluationIds(full);
  const filteredEvaluations = (evaluations || []).filter((evaluation) =>
    relevantEvaluationIds.has(evaluation._id)
  );

  await dispatch(fetchCertificatesListByUser(username));
  const certificates = selectCertificateList(store.getState()) as { courseName: string; hash: string }[];
  const courseTitle = (full?.course as { title?: string } | undefined)?.title;
  const certificateMatch = certificates?.find((certificate) => certificate.courseName === courseTitle);

  let certificado = null;
  if (certificateMatch?.hash) {
    await dispatch(fetchProgressAuditCertificateByHash(certificateMatch.hash));
    certificado = selectProgressAuditCertificate(store.getState());
  }

  return {
    course: full?.course ?? null,
    topics: full?.topics ?? [],
    lessons: full?.lessons ?? [],
    evaluations: filteredEvaluations,
    progress: full?.progress ?? null,
    certificado,
    traces,
  };
};
